/**
 * Client-side session expiry gate + activity based sliding refresh.
 *
 * - If JWT exp has passed: force re-login on focus / any interaction / API request.
 * - If JWT is still valid, user was recently active, and expiry is near: refresh.
 */

export const SESSION_REFRESH_THRESHOLD_MS = 60 * 60 * 1000 // refresh when ≤60m left
export const SESSION_ACTIVITY_WINDOW_MS = 15 * 60 * 1000 // must have activity in last 15m
export const SESSION_REFRESH_THROTTLE_MS = 5 * 60 * 1000 // preferred min gap between refreshes
export const SESSION_REFRESH_THROTTLE_FLOOR_MS = 5 * 1000 // never refresh more often than this
export const SESSION_ABSOLUTE_MAX_MS = 7 * 24 * 60 * 60 * 1000 // mirror API 7-day cap

export function refreshThrottleMs(msLeft: number): number {
  return Math.min(
    SESSION_REFRESH_THROTTLE_MS,
    Math.max(SESSION_REFRESH_THROTTLE_FLOOR_MS, Math.floor(msLeft / 2)),
  )
}

export type JwtSessionClaims = {
  exp: number
  iat?: number
  orig_iat?: number
}

type SessionManagerOptions = {
  getToken: () => string | null
  onExpired: () => void
  onRefreshed: (token: string, user: Record<string, unknown>) => void
  refreshToken: () => Promise<{ token: string; user: Record<string, unknown> }>
  isEnabled: () => boolean
}

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/')
  const padLength = (4 - (padded.length % 4)) % 4
  const withPad = padded + '='.repeat(padLength)
  return atob(withPad)
}

export function decodeJwtPayload(token: string): JwtSessionClaims | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) {
      return null
    }
    const json = base64UrlDecode(parts[1])
    const payload = JSON.parse(json) as Record<string, unknown>
    const exp = payload.exp
    if (typeof exp !== 'number') {
      return null
    }
    return {
      exp,
      iat: typeof payload.iat === 'number' ? payload.iat : undefined,
      orig_iat:
        typeof payload.orig_iat === 'number' ? payload.orig_iat : undefined,
    }
  } catch {
    return null
  }
}

export function getTokenExpiryMs(token: string): number | null {
  const claims = decodeJwtPayload(token)
  if (!claims) {
    return null
  }
  return claims.exp * 1000
}

export function isTokenExpired(
  token: string,
  nowMs: number = Date.now(),
): boolean {
  const expMs = getTokenExpiryMs(token)
  if (expMs == null) {
    // Malformed / undecodable token → treat as expired
    return true
  }
  return nowMs >= expMs
}

export function isPastAbsoluteMax(
  token: string,
  nowMs: number = Date.now(),
): boolean {
  const claims = decodeJwtPayload(token)
  if (!claims) {
    return true
  }
  const startSec = claims.orig_iat ?? claims.iat
  if (startSec == null) {
    return false
  }
  return nowMs - startSec * 1000 > SESSION_ABSOLUTE_MAX_MS
}

export function createSessionManager(options: SessionManagerOptions) {
  let lastActivityAt = Date.now()
  let lastRefreshAttemptAt = 0
  let refreshInFlight: Promise<void> | null = null
  let expTimer: ReturnType<typeof setTimeout> | null = null
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let started = false
  let forcingExpiry = false

  const markActivity = () => {
    lastActivityAt = Date.now()
  }

  const clearExpTimer = () => {
    if (expTimer != null) {
      clearTimeout(expTimer)
      expTimer = null
    }
  }

  const clearRefreshTimer = () => {
    if (refreshTimer != null) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  const clearTimers = () => {
    clearExpTimer()
    clearRefreshTimer()
  }

  const forceExpired = () => {
    if (forcingExpiry) {
      return
    }
    forcingExpiry = true
    clearTimers()
    try {
      options.onExpired()
    } finally {
      forcingExpiry = false
    }
  }

  const scheduleExpTimer = () => {
    clearExpTimer()
    if (!options.isEnabled()) {
      return
    }
    const token = options.getToken()
    if (!token) {
      return
    }
    const expMs = getTokenExpiryMs(token)
    if (expMs == null) {
      forceExpired()
      return
    }
    const delay = expMs - Date.now()
    if (delay <= 0) {
      forceExpired()
      return
    }
    const clamped = Math.min(delay, 2_147_483_647)
    expTimer = setTimeout(() => {
      if (!options.isEnabled()) {
        return
      }
      const current = options.getToken()
      if (current && isTokenExpired(current)) {
        forceExpired()
      } else {
        scheduleExpTimer()
      }
    }, clamped)
  }

  const scheduleRefreshTimer = () => {
    clearRefreshTimer()
    if (!options.isEnabled()) {
      return
    }
    const token = options.getToken()
    if (!token) {
      return
    }
    const expMs = getTokenExpiryMs(token)
    if (expMs == null) {
      return
    }
    const msLeft = expMs - Date.now()
    if (msLeft <= 0) {
      return
    }
    const delay =
      msLeft > SESSION_REFRESH_THRESHOLD_MS
        ? msLeft - SESSION_REFRESH_THRESHOLD_MS
        : refreshThrottleMs(msLeft)
    const clamped = Math.min(
      Math.max(delay, SESSION_REFRESH_THROTTLE_FLOOR_MS),
      2_147_483_647,
    )
    refreshTimer = setTimeout(() => {
      maybeRefresh()
      scheduleRefreshTimer()
    }, clamped)
  }

  const maybeRefresh = () => {
    if (!options.isEnabled()) {
      return
    }
    const token = options.getToken()
    if (!token) {
      return
    }
    if (isTokenExpired(token) || isPastAbsoluteMax(token)) {
      forceExpired()
      return
    }

    const expMs = getTokenExpiryMs(token)
    if (expMs == null) {
      forceExpired()
      return
    }

    const now = Date.now()
    const msLeft = expMs - now
    const recentlyActive = now - lastActivityAt <= SESSION_ACTIVITY_WINDOW_MS
    const nearExpiry = msLeft <= SESSION_REFRESH_THRESHOLD_MS
    const throttled = now - lastRefreshAttemptAt < refreshThrottleMs(msLeft)

    if (!recentlyActive || !nearExpiry || throttled || refreshInFlight) {
      return
    }

    lastRefreshAttemptAt = now
    refreshInFlight = options
      .refreshToken()
      .then(({ token: newToken, user }) => {
        options.onRefreshed(newToken, user)
        scheduleExpTimer()
        scheduleRefreshTimer()
      })
      .catch(() => {
        const current = options.getToken()
        if (!current || isTokenExpired(current)) {
          forceExpired()
        }
      })
      .finally(() => {
        refreshInFlight = null
      })
  }

  const checkExpiryOnResume = () => {
    if (!options.isEnabled()) {
      return
    }
    const token = options.getToken()
    if (!token) {
      return
    }
    if (isTokenExpired(token) || isPastAbsoluteMax(token)) {
      forceExpired()
      return
    }
    markActivity()
    maybeRefresh()
    scheduleExpTimer()
    scheduleRefreshTimer()
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkExpiryOnResume()
    }
  }

  const onInteraction = (event: Event) => {
    if (!options.isEnabled()) {
      return
    }
    const token = options.getToken()
    if (!token) {
      return
    }
    if (isTokenExpired(token) || isPastAbsoluteMax(token)) {
      event.preventDefault()
      event.stopPropagation()
      forceExpired()
      return
    }
    markActivity()
    maybeRefresh()
  }

  const start = () => {
    if (started || typeof window === 'undefined') {
      return
    }
    started = true
    markActivity()
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', checkExpiryOnResume)
    window.addEventListener('pageshow', checkExpiryOnResume)
    document.addEventListener('pointerdown', onInteraction, true)
    document.addEventListener('keydown', onInteraction, true)
    scheduleExpTimer()
    scheduleRefreshTimer()
    maybeRefresh()
  }

  const stop = () => {
    if (!started || typeof window === 'undefined') {
      return
    }
    started = false
    clearTimers()
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('focus', checkExpiryOnResume)
    window.removeEventListener('pageshow', checkExpiryOnResume)
    document.removeEventListener('pointerdown', onInteraction, true)
    document.removeEventListener('keydown', onInteraction, true)
  }

  const notifyTokenChanged = () => {
    markActivity()
    scheduleExpTimer()
    scheduleRefreshTimer()
    maybeRefresh()
  }

  const assertValidForRequest = (): boolean => {
    if (!options.isEnabled()) {
      return true
    }
    const token = options.getToken()
    if (!token) {
      return true
    }
    if (isTokenExpired(token) || isPastAbsoluteMax(token)) {
      forceExpired()
      return false
    }
    return true
  }

  return {
    start,
    stop,
    notifyTokenChanged,
    assertValidForRequest,
    markActivity,
    maybeRefresh,
    forceExpired,
  }
}

export type SessionManager = ReturnType<typeof createSessionManager>
